import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Check, X,
  Headset, ShieldCheck, CloudCog, Network, DatabaseBackup, Compass,
} from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CtaSection } from "@/components/CtaSection";
import { IMAGES, SERVICES, TRUST_PILLS, WHY_US, STEPS } from "@/lib/data";

const ICONS = { Headset, ShieldCheck, CloudCog, Network, DatabaseBackup, Compass };

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const cardX = useTransform(sx, [-0.5, 0.5], [-16, 16]);
  const cardY = useTransform(sy, [-0.5, 0.5], [-12, 12]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative overflow-hidden pt-14 pb-20 sm:pt-20 sm:pb-28"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 55% 45% at 72% 18%, hsl(var(--primary) / 0.08), transparent 70%)" }}
        aria-hidden="true"
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-6"
            >
              Managed IT in Glasgow, Edinburgh & the Central Belt
            </motion.p>
            <h1
              data-testid="hero-headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06]"
            >
              <MaskedLine delay={0.15}>Technology</MaskedLine>
              <MaskedLine delay={0.28}>that just</MaskedLine>
              <MaskedLine delay={0.41}>
                <em className="not-italic text-primary">works.</em>
              </MaskedLine>
            </h1>
            <motion.p
              data-testid="hero-subheadline"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-muted-foreground"
            >
              Calm, reliable IT management and cyber security for growing businesses
              across Scotland's Central Belt, from people who answer when you call.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact?intent=review"
                data-testid="hero-request-review-button"
                className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 font-semibold text-primary-foreground transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 active:scale-[0.98]"
              >
                Request a Free IT Review
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-explore-services-button"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-7 py-3.5 font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:border-foreground/30 active:scale-[0.98]"
              >
                Explore Services
              </Link>
            </motion.div>
            <motion.ul
              data-testid="hero-trust-pills"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.95 }}
              className="mt-10 flex flex-wrap gap-2.5"
            >
              {TRUST_PILLS.map((pill) => (
                <li
                  key={pill}
                  className="rounded-full border border-border bg-muted/60 px-4 py-1.5 text-xs font-medium text-muted-foreground"
                >
                  {pill}
                </li>
              ))}
            </motion.ul>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-3xl border border-border shadow-2xl shadow-foreground/10">
                <motion.img
                  src={IMAGES.workspace}
                  alt="Groundbreaking IT engineers at work in a Central Belt office"
                  style={{ y: imgY, scale: 1.15 }}
                  className="h-[420px] w-full object-cover"
                  data-testid="hero-image"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: "radial-gradient(ellipse 90% 60% at 30% 10%, transparent 40%, hsl(var(--foreground) / 0.35) 100%)" }}
                  aria-hidden="true"
                />
              </div>
              <motion.div
                style={{ x: cardX, y: cardY }}
                data-testid="hero-status-card"
                className="absolute -bottom-6 -left-4 sm:-left-8 rounded-2xl border border-border bg-card/90 backdrop-blur-md px-5 py-4 shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <span className="relative h-2.5 w-2.5 rounded-full bg-green-500 ping-dot" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold leading-tight">All systems operational</p>
                    <p className="text-xs text-muted-foreground">15 min average response · live now</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicesBento = () => (
  <section data-testid="services-overview" className="py-16 sm:py-24 lg:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-2xl mb-12">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-4">What we do</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
          Practical IT services, built for growing businesses
        </h2>
        <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
          Clear scope, no hidden fees, and every service described by what it does for
          your business, not by its acronym.
        </p>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-6 lg:gap-8">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          return (
            <Reveal key={s.id} delay={i * 0.06} className={`col-span-1 ${s.span}`}>
              <Link
                to={`/services#${s.id}`}
                data-testid={`service-card-${s.id}`}
                className="group flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5"
              >
                <span className="mb-5 grid h-11 w-11 place-items-center rounded-xl bg-forest text-blue-300 transition-transform duration-300 group-hover:scale-110">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="text-lg sm:text-xl font-semibold tracking-tight">{s.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{s.plain}</p>
                <p className="mt-4 pt-4 border-t border-border text-sm font-medium flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <span>{s.outcome}</span>
                </p>
                <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground transition-colors group-hover:text-primary">
                  Learn more
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section data-testid="why-us-section" className="py-16 sm:py-24 bg-muted/40 border-y border-border">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-2xl mb-12">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-4">Why us</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
          A smaller MSP, on purpose
        </h2>
        <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
          We are not trying to look like a corporation. We are a local team that relies
          on clarity, responsiveness and honest advice.
        </p>
      </Reveal>
      <div className="grid gap-6 lg:grid-cols-2">
        <Reveal>
          <div className="h-full rounded-2xl border border-border bg-card p-8">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              The typical MSP experience
            </p>
            <ul className="space-y-4">
              {WHY_US.legacy.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <X className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="h-full rounded-2xl bg-forest p-8 noise-overlay relative overflow-hidden">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 mb-6">
              The Groundbreaking way
            </p>
            <ul className="space-y-4">
              {WHY_US.ours.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-white/85">
                  <Check className="h-4 w-4 mt-0.5 shrink-0 text-white" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const HowWeWork = () => (
  <section data-testid="how-we-work-section" className="py-16 sm:py-24 lg:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-2xl mb-12">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-4">How we work</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
          Five steps, no surprises
        </h2>
        <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
          Switching IT provider feels risky, so we make the whole process visible
          before you commit to anything.
        </p>
      </Reveal>
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
        {STEPS.map((s, i) => (
          <Reveal key={s.step} delay={i * 0.08}>
            <div data-testid={`process-step-${s.name.toLowerCase()}`} className="border-t-2 border-border pt-6 transition-colors duration-300 hover:border-primary">
              <p className="font-mono text-3xl font-semibold text-primary/70">{s.step}</p>
              <h3 className="mt-3 text-lg font-semibold tracking-tight">{s.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <ServicesBento />
      <WhyUs />
      <HowWeWork />
      <CtaSection />
    </>
  );
}
